import { MasterDataRepository } from 'src/core/master-data/domain/master-data-repository';
import { MasterData } from 'src/core/master-data/domain/master-data';
import { HttpClient } from 'src/core/common/domain/http/http-client';
import { Timezone } from 'src/core/common/domain/date/timezone';
import { ServiceAddress } from 'src/core/master-data/domain/service-address';
import { Ambit } from 'src/core/master-data/domain/ambit';
import { Injectable } from '@angular/core';
import { Assignee, FilterDate, Filters } from 'src/core/master-data/domain/filters';
import { Location, Task } from 'src/core/master-data/domain/task';
import { TaskStatus } from 'src/core/master-data/domain/task-status';
import { ApiTaskResponse } from './api-task-response';
import { availableActionsFromApi } from './api-available-actions';
import { CloseTaskActions, CloseTaskActionsData } from 'src/core/master-data/domain/close-task-actions';
import { ApiGetMasterDataResponse } from './api-master-get-data-response';
import { DefaultFilters } from 'src/core/master-data/domain/default-filters';
import { WorkType } from 'src/core/master-data/domain/work-type';
import { Priority } from 'src/core/master-data/domain/priority';
import { ApiGetMasterDataCloseTaskActionsResponse } from './api-get-master-data-close-task-actions-response';
import { TranslatorService } from 'src/core/translator/domain/translator-service';
import {
  MasterDataConfiguration,
  MasterDataConfigurationData,
} from 'src/core/master-data/domain/master-data-configuration';
import { NfcToolsConfiguration } from 'src/core/master-data/domain/nfc-tools-configuration';
import { ApiService } from 'src/core/api/domain/api.service';
import { ExceptionManagerService } from 'src/core/common/domain/exceptions/exception-manager.service';
import { ApiAccessService } from 'src/core/common/domain/api-access/api-access.service';
import { RejectTaskActions, RejectTaskActionsData } from 'src/core/master-data/domain/reject-task-actions';
import { ApiGetMasterDataRejectTaskActionsResponse } from './api-get-master-data-reject-task-actions-response';
import { ApiGetMasterDataConfigurationResponse } from './api-get-master-data-configuration-response';
import { ApiGetMasterDataFiltersResponse } from './api-get-master-data-filters-response';
import { NoConnectionException } from 'src/core/common/domain/exceptions/no-connection-exception';
import { ConnectivityService } from 'src/core/connection/domain/connectivity.service';
import { GeolocationHelper } from 'src/core/common/domain/geolocation/geolocation-helper';
import { ApiGetMasterDataConfirmCloseTaskActionsResponse } from './api-get-master-data-confirm-close-task-actions-response';
import {
  ConfirmCloseTaskActions,
  ConfirmCloseTaskActionsData,
} from 'src/core/master-data/domain/confirm-close-task-actions';
import {
  ConfirmRejectTaskActions,
  ConfirmRejectTaskActionsData,
} from 'src/core/master-data/domain/confirm-reject-task-actions';
import { ApiGetMasterDataConfirmRejectTaskActionsResponse } from './api-get-master-data-confirm-reject-task-actions-response';
import { MasterDataBadFormatException } from 'src/core/common/domain/exceptions/master-data-bad-format-exception';
import { ApiException } from 'src/core/common/domain/exceptions/api-exception';
import { HttpErrorResponse } from '@angular/common/http';
import { PlatformService } from 'src/core/common/domain/platform/platform.service';
import { RequestTimeouts } from 'src/core/common/infrastructure/http/request-timeouts';
import { TimeoutException } from 'src/core/common/domain/exceptions/timeout-exception';

@Injectable({
  providedIn: 'any',
})
export class HttpMasterDataRepository extends MasterDataRepository {
  constructor(
    private readonly apiAccessService: ApiAccessService,
    private readonly apiService: ApiService,
    private readonly client: HttpClient,
    private readonly connectivityService: ConnectivityService,
    private readonly exceptionManager: ExceptionManagerService,
    private readonly platformService: PlatformService,
    private readonly translation: TranslatorService
  ) {
    super();
  }

  async load(): Promise<MasterData> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const url = `${this.apiService.baseUrl()}/master-data.get`;
      const response = await this.client.get<ApiGetMasterDataResponse>(url, {}, {}, true, true);
      const masterData = this.buildMasterDataResponse(response);
      return masterData;
    } catch (exception) {
      if (this.requestStalled(exception)) {
        throw TimeoutException.fromError(exception);
      }

      const isApiException = exception instanceof ApiException;
      const isHttpError = exception instanceof HttpErrorResponse;
      if (!isApiException && !isHttpError) {
        // we assume that if we get a javascript error, it is caused by a problem with master data format
        const error = MasterDataBadFormatException.fromError(exception);
        const errorMessage = this.translation.translate('MESSAGES.MASTER_DATA_BAD_FORMAT', { code: error.code });
        error.overrideMessage(errorMessage);
        throw error;
      }

      throw this.exceptionManager.manage(exception);
    }
  }

  // A stalled request is mapped here and not in ExceptionManagerService because doing it there would also change
  // how task actions react to it, and that behaviour is agreed with the client.
  private requestStalled(exception: unknown): exception is HttpErrorResponse {
    return exception instanceof HttpErrorResponse && exception.statusText === RequestTimeouts.STALLED_STATUS_TEXT;
  }

  private buildMasterDataResponse(response: ApiGetMasterDataResponse) {
    console.log('Processing received master data: serviceAddresses');
    const serviceAddresses: Array<ServiceAddress> = this.buildServiceAddressListFromApiResponse(response);
    console.log('Processing received master data: ambitList');
    const ambitList: Array<Ambit> = this.buildAmbitListFromApiResponse(response);
    console.log('Processing received master data: configuration');
    const configuration = this.buildConfigurationFromApiResponse(response.configuration);
    console.log('Processing received master data: filters');
    const filters: Filters = this.buildFiltersFromApiResponse(response);
    console.log('Processing received master data: tasks');
    const tasks: Array<Task> = this.buildTasksFromApiResponse(response, configuration, filters);
    console.log('Processing received master data: availableActions');
    const availableActions = availableActionsFromApi(response.available_actions);
    console.log('Processing received master data: closeTaskActions');
    const closeTaskActions = this.buildCloseTaskActionsFromApiResponse(response.close_task_actions);
    console.log('Processing received master data: confirmCloseTaskActions');
    const confirmCloseTaskActions = this.buildConfirmCloseTaskActionsFromApiResponse(
      response.confirm_close_task_actions
    );
    console.log('Processing received master data: rejectTaskActions');
    const rejectTaskActions = this.buildRejectTaskActionsFromApiResponse(response.reject_task_actions);
    console.log('Processing received master data: confirmRejectTaskActions');
    const confirmRejectTaskActions = this.buildConfirmRejectTaskActionsFromApiResponse(
      response.confirm_reject_task_actions
    );
    console.log('Processing received master data completed!');

    return new MasterData(
      serviceAddresses,
      filters,
      ambitList,
      tasks,
      availableActions,
      closeTaskActions,
      confirmCloseTaskActions,
      rejectTaskActions,
      confirmRejectTaskActions,
      configuration,
      false
    );
  }

  private buildCloseTaskActionsFromApiResponse(
    closeTaskActions: ApiGetMasterDataCloseTaskActionsResponse
  ): CloseTaskActions {
    return new CloseTaskActions(this.prepareCloseTaskActions(closeTaskActions));
  }

  private prepareCloseTaskActions(
    inputCloseTaskActions: ApiGetMasterDataCloseTaskActionsResponse
  ): CloseTaskActionsData {
    const signatureOptions = inputCloseTaskActions.fields.signature.options;
    const signatureOptionsWithLabels = signatureOptions.map((o) => {
      const key = `TASK.CLOSE_TASK_MODAL.SIGNATURE_OPTIONS.${o.id.toUpperCase()}`;
      let label = this.translation.translate(key);
      if (label === key) {
        label = o.id;
      }
      return { id: o.id, label };
    });

    if (inputCloseTaskActions.fields.predefined_observations.show === undefined) {
      inputCloseTaskActions.fields.predefined_observations.show = false;
    }

    if (inputCloseTaskActions.fields.resolutions && inputCloseTaskActions.fields.resolutions.show === undefined) {
      inputCloseTaskActions.fields.resolutions.show = false;
    }

    // transform { code, name } to { id, name }
    const transformedResolutions = inputCloseTaskActions.fields.resolutions.options.map((option) => ({
      id: option.id,
      name: option.name,
      isFinal: option.is_final,
    }));

    // set show_bulk_mode to false by default if not informed
    this.establishFallbackBulkModeForCloseTaskActions(inputCloseTaskActions);

    const closeTaskActions = JSON.parse(JSON.stringify(inputCloseTaskActions)) as CloseTaskActionsData;
    closeTaskActions.fields.signature.options = signatureOptionsWithLabels;
    closeTaskActions.fields.resolutions.options = transformedResolutions;

    return closeTaskActions;
  }

  private buildConfirmCloseTaskActionsFromApiResponse(
    confirmCloseTaskActions: ApiGetMasterDataConfirmCloseTaskActionsResponse
  ): ConfirmCloseTaskActions {
    return new ConfirmCloseTaskActions(this.prepareConfirmCloseTaskActions(confirmCloseTaskActions));
  }

  private prepareConfirmCloseTaskActions(
    inputConfirmCloseTaskActions: ApiGetMasterDataConfirmCloseTaskActionsResponse
  ): ConfirmCloseTaskActionsData {
    if (
      inputConfirmCloseTaskActions.fields.resolutions &&
      inputConfirmCloseTaskActions.fields.resolutions.show === undefined
    ) {
      inputConfirmCloseTaskActions.fields.resolutions.show = false;
    }

    // transform { code, name, is_final } to { id, name, isFinal }
    const transformedResolutions = inputConfirmCloseTaskActions.fields.resolutions.options.map((option) => ({
      id: option.id,
      name: option.name,
      isFinal: option.is_final,
    }));

    const confirmCloseTaskActions = JSON.parse(
      JSON.stringify(inputConfirmCloseTaskActions)
    ) as ConfirmCloseTaskActionsData;
    confirmCloseTaskActions.fields.resolutions.options = transformedResolutions;

    return confirmCloseTaskActions;
  }

  private establishFallbackBulkModeForCloseTaskActions(
    inputCloseTaskActions: ApiGetMasterDataCloseTaskActionsResponse
  ) {
    inputCloseTaskActions.fields.observations_for_client.show_in_bulk =
      inputCloseTaskActions.fields.observations_for_client.show_in_bulk ?? true;
    inputCloseTaskActions.fields.predefined_observations.show_in_bulk =
      inputCloseTaskActions.fields.predefined_observations.show_in_bulk ?? false;
    inputCloseTaskActions.fields.resolutions.show_in_bulk =
      inputCloseTaskActions.fields.resolutions.show_in_bulk ?? false;
    inputCloseTaskActions.fields.emails.show_in_bulk = inputCloseTaskActions.fields.emails.show_in_bulk ?? false;
    inputCloseTaskActions.fields.emails.autofill_with_service_address_recipient_emails =
      inputCloseTaskActions.fields.emails.autofill_with_service_address_recipient_emails ?? true;
    inputCloseTaskActions.fields.attachments.show_in_bulk =
      inputCloseTaskActions.fields.attachments.show_in_bulk ?? false;
    inputCloseTaskActions.fields.signature.show_in_bulk = inputCloseTaskActions.fields.signature.show_in_bulk ?? false;
  }

  private establishFallbackBulkModeForRejectTaskActions(
    inputRejectTaskActions: ApiGetMasterDataRejectTaskActionsResponse
  ) {
    inputRejectTaskActions.fields.observations_for_client.show_in_bulk =
      inputRejectTaskActions.fields.observations_for_client.show_in_bulk ?? true;
    inputRejectTaskActions.fields.resolutions.show_in_bulk =
      inputRejectTaskActions.fields.resolutions.show_in_bulk ?? false;
    inputRejectTaskActions.fields.predefined_observations.show_in_bulk =
      inputRejectTaskActions.fields.predefined_observations.show_in_bulk ?? false;
    inputRejectTaskActions.fields.attachments.show_in_bulk =
      inputRejectTaskActions.fields.attachments.show_in_bulk ?? false;
  }

  private buildRejectTaskActionsFromApiResponse(
    rejectTaskActions: ApiGetMasterDataRejectTaskActionsResponse
  ): RejectTaskActions {
    return new RejectTaskActions(this.prepareRejectTaskActions(rejectTaskActions));
  }

  private prepareRejectTaskActions(
    inputRejectTaskActions: ApiGetMasterDataRejectTaskActionsResponse
  ): RejectTaskActionsData {
    if (!inputRejectTaskActions.fields.attachments) {
      inputRejectTaskActions.fields.attachments = {
        show: false,
        show_in_bulk: false,
      };
    }

    if (inputRejectTaskActions.fields.predefined_observations.show === undefined) {
      inputRejectTaskActions.fields.predefined_observations.show = false;
    }

    if (inputRejectTaskActions.fields.resolutions && inputRejectTaskActions.fields.resolutions.show === undefined) {
      inputRejectTaskActions.fields.resolutions.show = false;
    }

    if (inputRejectTaskActions.fields.attachments.show === undefined) {
      inputRejectTaskActions.fields.attachments.show = false;
    }

    const transformedResolutions = inputRejectTaskActions.fields.resolutions.options.map((option) => ({
      id: option.id,
      name: option.name,
      isFinal: option.is_final,
    }));

    // set show_bulk_mode to false by default if not informed
    this.establishFallbackBulkModeForRejectTaskActions(inputRejectTaskActions);

    const rejectTaskActions = JSON.parse(JSON.stringify(inputRejectTaskActions)) as RejectTaskActionsData;
    rejectTaskActions.fields.resolutions.options = transformedResolutions;

    return rejectTaskActions;
  }

  private buildConfirmRejectTaskActionsFromApiResponse(
    confirmRejectTaskActions: ApiGetMasterDataConfirmRejectTaskActionsResponse
  ): ConfirmRejectTaskActions {
    return new ConfirmRejectTaskActions(this.prepareConfirmRejectTaskActions(confirmRejectTaskActions));
  }

  private prepareConfirmRejectTaskActions(
    inputConfirmRejectTaskActions: ApiGetMasterDataConfirmRejectTaskActionsResponse
  ): ConfirmRejectTaskActionsData {
    if (
      inputConfirmRejectTaskActions.fields.resolutions &&
      inputConfirmRejectTaskActions.fields.resolutions.show === undefined
    ) {
      inputConfirmRejectTaskActions.fields.resolutions.show = false;
    }

    const transformedResolutions = inputConfirmRejectTaskActions.fields.resolutions.options.map((option) => ({
      id: option.id,
      name: option.name,
      isFinal: option.is_final,
    }));

    const confirmRejectTaskActions = JSON.parse(
      JSON.stringify(inputConfirmRejectTaskActions)
    ) as ConfirmRejectTaskActionsData;
    confirmRejectTaskActions.fields.resolutions.options = transformedResolutions;

    return confirmRejectTaskActions;
  }

  private buildServiceAddressListFromApiResponse(response: ApiGetMasterDataResponse): Array<ServiceAddress> {
    return response.service_addresses.map((serviceAddress) =>
      ServiceAddress.fromPrimitives({
        id: serviceAddress.service_address_id,
        name: serviceAddress.name,
        recipient_emails: serviceAddress.recipient_emails,
        create_task_task_types: serviceAddress.create_task_task_types,
        available_actions: availableActionsFromApi(serviceAddress.available_actions),
      })
    );
  }

  private buildAmbitListFromApiResponse(response: ApiGetMasterDataResponse) {
    return response.ambit.list
      .filter(
        (ambit) =>
          ambit.full_ambit_printable !== null &&
          ambit.full_ambit_printable !== undefined &&
          ambit.full_ambit_printable.trim() !== ''
      ) // discard NULL full_ambit_printable
      .map((ambit) => {
        const code = ambit.code === '' ? null : ambit.code;
        return new Ambit(
          ambit.id,
          code,
          ambit.name,
          ambit.full_ambit_printable,
          ambit.parent_id,
          ambit.service_address_id
        );
      })
      .sort((a, b) => a.fullAmbitPrintable.localeCompare(b.fullAmbitPrintable));
  }

  private buildFiltersFromApiResponse(response: ApiGetMasterDataResponse): Filters {
    // TODO: Ensure all values are correct?
    this.sanitizeDefaultDateFilter(response.filters);

    const defaultFilters = new DefaultFilters(
      response.filters.default.work_types,
      response.filters.default.status,
      response.filters.default.assignee as Array<Assignee>,
      response.filters.default.date as FilterDate,
      response.filters.default.priority ?? [],
      response.filters.default.route ?? []
    );

    // API fallback (to prevent lack of field "work_type.name")
    this.applyWorkTypeNamesApiFallback(response.filters.work_types);

    const datesConfiguration = response.filters.date ?? { max_days_range: MasterDataRepository.DEFAULT_MAX_DAYS_RANGE };

    return new Filters(
      defaultFilters,
      response.filters.work_types.map((work_type) => WorkType.fromPrimitives(work_type)),
      response.filters.status.map((status) => TaskStatus.fromPrimitives(status.id)),
      response.filters.assignee.map((assignee) => assignee.id as Assignee),
      datesConfiguration,
      (response.filters.priorities ?? []).map((priority) => Priority.fromPrimitives(priority)),
      response.filters.route ?? []
    );
  }

  private buildTasksFromApiResponse(
    response: ApiGetMasterDataResponse,
    configuration: MasterDataConfiguration,
    filters: Filters
  ): Array<Task> {
    return response.tasks.map((task) => {
      if (!this.platformService.isRealMobileDevice()) {
        // console.log('digesting task: ', task.id);
      }

      const _task = this.buildTaskFromApiResponse(task, configuration, filters);

      if (!this.platformService.isRealMobileDevice()) {
        // console.log('digested task: ', _task.id);
      }
      return _task;
    });
  }

  private buildTaskFromApiResponse(task: ApiTaskResponse, configuration: MasterDataConfiguration, filters: Filters) {
    const taskDate = {
      expected: task.planned_date.expected,
      from: task.planned_date.valid_range_start,
      to: task.planned_date.valid_range_end,
      timezone: Timezone.firstSupported(task.timezone, configuration.getTimezone()),
    };
    const taskLocation: Location = {
      full_address: '' + task.location.full_address, // force string (temp fix for response being a number)
      geolocation: GeolocationHelper.parseFromAPI(task.location),
    };

    const workType = this.resolveWorkType(task.work_type, filters);
    const taskType = task.task_type ? { id: task.task_type.id, name: task.task_type.name } : null;
    const priority = task.priority ? { id: task.priority.id, name: task.priority.name } : null;
    const route = task.route ? { id: task.route.id, name: task.route.name } : null;
    const displayServiceAddressInTask = configuration.isDisplayServiceAddressInTask();
    const displayPostalAddressInImmediateTask = configuration.isDisplayPostalAddressInImmediateTask();
    const displayPostalAddressInPlannedTask = configuration.isDisplayPostalAddressInPlannedTask();
    const displayAmbitInImmediateTask = configuration.isDisplayAmbitInImmediateTask();
    const displayAmbitInPlannedTask = configuration.isDisplayAmbitInPlannedTask();
    const displayDescriptionInImmediateTask = configuration.isDisplayDescriptionInImmediateTask();
    const displayDescriptionInPlannedTask = configuration.isDisplayDescriptionInPlannedTask();
    const displayInlineObservationsForClient = configuration.isDisplayInlineObservationsForClient();

    return Task.fromPrimitives({
      id: task.id,
      status: task.status,
      subject: task.subject,
      description: task.description,
      description_files: task.description_files ?? [],
      observations_for_client: task.observations_for_client,
      work_type: workType.toPrimitives(),
      task_type: taskType,
      priority,
      route,
      ambit: task.ambit,
      date: taskDate,
      assignee: task.assignee,
      frequency: task.frequency,
      resolution: task.resolution ?? null,
      must_be_modified_by_assignee: task.must_be_modified_by_assignee,
      location: taskLocation,
      started_at: task.started_at,
      updated_at: task.updated_at,
      closed_at: task.closed_at,
      confirm_closed_at: null,
      rejected_at: null,
      confirm_rejected_at: null,
      created_at: task.created_at,
      attachments: [],
      display_service_address_in_task: displayServiceAddressInTask,
      display_postal_address_in_immediate_task: displayPostalAddressInImmediateTask,
      display_postal_address_in_planned_task: displayPostalAddressInPlannedTask,
      display_ambit_in_immediate_task: displayAmbitInImmediateTask,
      display_ambit_in_planned_task: displayAmbitInPlannedTask,
      display_description_in_immediate_task: displayDescriptionInImmediateTask,
      display_description_in_planned_task: displayDescriptionInPlannedTask,
      display_inline_observations_for_client: displayInlineObservationsForClient,
      close_task_data: null,
      reject_task_data: null,
      confirm_close_task_data: null,
      confirm_reject_task_data: null,
      in_final_state: false,
      available_actions: availableActionsFromApi(task.available_actions),
    });
  }

  /*
   * A task's payload carries only the work type id and name; whether it is planned and where it
   * sorts live in the master data catalog, so the task's work type is completed from there. An id
   * the catalog does not know keeps its own name and falls back to the last position.
   */
  private resolveWorkType(payload: { id: string; name?: string }, filters: Filters): WorkType {
    const catalogued = filters.findWorkType(payload.id);
    if (catalogued) {
      return catalogued;
    }

    this.applyWorkTypeNameApiFallback(payload);
    return WorkType.fromPrimitives({ id: payload.id, name: payload.name });
  }

  private applyWorkTypeNamesApiFallback(workTypes: Array<{ id: string; name?: string }>) {
    workTypes.map((workType) => this.applyWorkTypeNameApiFallback(workType));
  }

  // TODO: Extract duplicated method or delete
  private applyWorkTypeNameApiFallback(workType: { id: string; name?: string }) {
    if (!workType.name) {
      const key = `FILTERS.WORK_TYPES.${workType.id.toUpperCase()}`;
      let workTypeName = this.translation.translate(key);
      if (workTypeName === key) {
        workTypeName = workType.id;
      }
      workType.name = workTypeName;
    }
  }

  private buildConfigurationFromApiResponse(configurationResponse: ApiGetMasterDataConfigurationResponse) {
    this.applyWorkTypeNamesApiFallback(configurationResponse.create_task_work_types);

    const configuration: MasterDataConfigurationData = {
      display_service_address_in_task: configurationResponse.display_service_address_in_task ?? true,
      display_postal_address_in_immediate_task: configurationResponse.display_postal_address_in_immediate_task ?? true,
      display_postal_address_in_planned_task: configurationResponse.display_postal_address_in_planned_task ?? true,
      display_ambit_in_immediate_task: configurationResponse.display_ambit_in_immediate_task ?? true,
      display_ambit_in_planned_task: configurationResponse.display_ambit_in_planned_task ?? true,
      display_description_in_immediate_task: configurationResponse.display_description_in_immediate_task ?? true,
      display_description_in_planned_task: configurationResponse.display_description_in_planned_task ?? true,
      display_task_type_in_create_task: configurationResponse.display_task_type_in_create_task ?? false,
      max_attachments: configurationResponse.max_attachments,
      max_attachment_size: configurationResponse.max_attachment_size * 1_000, // transform KB into bytes
      max_request_size: (configurationResponse.max_request_size ?? 8) * 1_000_000, // transform MB into bytes
      can_create_tasks: configurationResponse.can_create_tasks,
      create_task_work_types: configurationResponse.create_task_work_types,
      create_task_priorities: configurationResponse.create_task_priorities ?? [],
      create_task_default_priority: configurationResponse.create_task_default_priority ?? null,
      observations_for_client_inline: configurationResponse.observations_for_client_inline ?? false,
      timezone: configurationResponse.timezone ?? null,
      presence_check: configurationResponse.presence_check ?? null,
      nfc_tools: NfcToolsConfiguration.fromPrimitives(configurationResponse.nfc_tools).toPrimitives(),
    };

    return new MasterDataConfiguration(configuration);
  }

  private sanitizeDefaultDateFilter(filters: ApiGetMasterDataFiltersResponse) {
    let sanitizedDefaultDateFilter = filters.default.date;
    if (sanitizedDefaultDateFilter === '7_days_before_and_after') {
      sanitizedDefaultDateFilter = FilterDate.RANGE_SEVEN_DAYS_BEFORE_AND_AFTER;
    }
    if (!Object.values(FilterDate).includes(sanitizedDefaultDateFilter as FilterDate)) {
      sanitizedDefaultDateFilter = FilterDate.TODAY;
    }
    filters.default.date = sanitizedDefaultDateFilter;
  }
}
